/**
 * Workflow Form Component
 * Form for entering transcription workflow parameters
 */

import React, { useState, useCallback } from 'react';
import {
  Card,
  Input,
  Button,
  Select,
  Radio,
  Stack,
  Flex,
  useTheme,
  spacing,
  textStyles,
  fontSize,
} from '@fti/design-system';
import { LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../../config/languages';

export type PromptMode = 'basic' | 'advanced';

export interface WorkflowFormData {
  videoUrl: string;
  streamerName: string;
  vodDate: string;
  promptTemplate: string;
  flaggedTerms: string;
  emailRecipients: string;
  /** ISO 639-1 language code for transcription; "" = auto-detect. */
  language: string;
  /** 'basic' = flagged topics on a generic prompt; 'advanced' = free-form prompt. */
  promptMode: PromptMode;
  /** Free-form prompt used when promptMode is 'advanced'. */
  customPrompt: string;
}

export interface WorkflowFormProps {
  onSubmit: (data: WorkflowFormData) => void;
  isSubmitting?: boolean;
  defaultPrompt?: string;
  showUrlField?: boolean;
  showPromptField?: boolean;
}

/**
 * Form component for workflow configuration
 * Handles URL input, streamer name, date, prompt, flagged terms, and email recipients
 */
export function WorkflowForm({
  onSubmit,
  isSubmitting = false,
  defaultPrompt = 'Summarize the following audio or video.',
  showUrlField = true,
  showPromptField = false,
}: WorkflowFormProps) {
  const { theme } = useTheme();

  const [formData, setFormData] = useState<WorkflowFormData>({
    videoUrl: '',
    streamerName: '',
    vodDate: new Date().toISOString().split('T')[0],
    promptTemplate: defaultPrompt,
    flaggedTerms: '',
    emailRecipients: '',
    language: DEFAULT_LANGUAGE,
    promptMode: 'basic',
    customPrompt: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof WorkflowFormData, string>>>({});

  const handleChange = useCallback((field: keyof WorkflowFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validate = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof WorkflowFormData, string>> = {};

    if (showUrlField && !formData.videoUrl.trim()) {
      newErrors.videoUrl = 'Video URL is required';
    } else if (showUrlField && formData.videoUrl.trim()) {
      // Basic URL validation
      try {
        const url = new URL(formData.videoUrl);
        const validDomains = ['kick.com', 'youtube.com', 'youtu.be', 'twitch.tv', 'podcasts.apple.com', 'gettr.com'];
        const isValidDomain = validDomains.some(domain => url.hostname.includes(domain));
        if (!isValidDomain) {
          newErrors.videoUrl = 'URL must be from Kick, YouTube, Twitch, Apple Podcasts, or GetTR';
        }
      } catch {
        newErrors.videoUrl = 'Please enter a valid URL';
      }
    }

    if (!formData.streamerName.trim()) {
      newErrors.streamerName = 'Creator name is required';
    }

    if (!formData.vodDate) {
      newErrors.vodDate = 'VOD date is required';
    }

    if (showPromptField && !formData.promptTemplate.trim()) {
      newErrors.promptTemplate = 'Prompt template is required';
    }

    if (formData.promptMode === 'advanced' && !formData.customPrompt.trim()) {
      newErrors.customPrompt = 'Please enter a prompt or switch to basic prompting';
    }

    // Validate email recipients if provided
    if (formData.emailRecipients.trim()) {
      const emails = formData.emailRecipients.split(',').map(e => e.trim());
      const invalidEmails = emails.filter(e => e && !e.includes('@'));
      if (invalidEmails.length > 0) {
        newErrors.emailRecipients = `Invalid email(s): ${invalidEmails.join(', ')}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, showUrlField, showPromptField]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  }, [formData, onSubmit, validate]);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {showUrlField && (
          <Stack gap="xs">
            <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
              Video URL <span style={{ color: errorColor }}>*</span>
            </label>
            <Input
              value={formData.videoUrl}
              onChange={(e) => handleChange('videoUrl', e.target.value)}
              placeholder="https://kick.com/video/..."
              disabled={isSubmitting}
            />
            {errors.videoUrl && (
              <span style={{ fontSize: fontSize.xs, color: errorColor }}>{errors.videoUrl}</span>
            )}
            <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
              Supported: Kick, YouTube, Twitch, Apple Podcasts, GetTR
            </span>
          </Stack>
        )}

        <Flex gap="md">
          <Stack gap="xs" style={{ flex: 1 }}>
            <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
              Creator Name <span style={{ color: errorColor }}>*</span>
            </label>
            <Input
              value={formData.streamerName}
              onChange={(e) => handleChange('streamerName', e.target.value)}
              placeholder="e.g., channel or podcast name"
              disabled={isSubmitting}
            />
            {errors.streamerName && (
              <span style={{ fontSize: fontSize.xs, color: errorColor }}>{errors.streamerName}</span>
            )}
          </Stack>

          <Stack gap="xs" style={{ flex: 1 }}>
            <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
              Date <span style={{ color: errorColor }}>*</span>
            </label>
            <Input
              type="date"
              value={formData.vodDate}
              onChange={(e) => handleChange('vodDate', e.target.value)}
              disabled={isSubmitting}
            />
            {errors.vodDate && (
              <span style={{ fontSize: fontSize.xs, color: errorColor }}>{errors.vodDate}</span>
            )}
          </Stack>
        </Flex>

        <Stack gap="xs">
          <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
            Transcription Language
          </label>
          <Select
            value={formData.language}
            onChange={(value) => handleChange('language', value)}
            disabled={isSubmitting}
            options={LANGUAGE_OPTIONS}
          />
          <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
            Optional: Auto-detect works for most content. Choose a language to force transcription in it.
          </span>
        </Stack>

        {showPromptField && (
          <Stack gap="xs">
            <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
              Prompt Template <span style={{ color: errorColor }}>*</span>
            </label>
            <textarea
              value={formData.promptTemplate}
              onChange={(e) => handleChange('promptTemplate', e.target.value)}
              placeholder="Enter the summarization prompt..."
              disabled={isSubmitting}
              rows={4}
              style={{
                width: '100%',
                padding: spacing.sm,
                borderRadius: '6px',
                border: `1px solid ${theme.colors.border.default}`,
                fontSize: fontSize.sm,
                fontFamily: 'inherit',
                resize: 'vertical',
                background: theme.colors.background.default,
                color: textColor,
              }}
            />
            {errors.promptTemplate && (
              <span style={{ fontSize: fontSize.xs, color: errorColor }}>{errors.promptTemplate}</span>
            )}
          </Stack>
        )}

        <Stack gap="sm">
          <Flex align="center" gap="md">
            <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
              Prompting
            </label>
            <Radio
              name="promptMode"
              value="basic"
              checked={formData.promptMode === 'basic'}
              onChange={() => handleChange('promptMode', 'basic')}
              label="Basic"
              disabled={isSubmitting}
            />
            <Radio
              name="promptMode"
              value="advanced"
              checked={formData.promptMode === 'advanced'}
              onChange={() => handleChange('promptMode', 'advanced')}
              label="Advanced"
              disabled={isSubmitting}
            />
          </Flex>

          {formData.promptMode === 'basic' ? (
            <Stack gap="xs">
              <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
                Flagged Topics
              </label>
              <Input
                value={formData.flaggedTerms}
                onChange={(e) => handleChange('flaggedTerms', e.target.value)}
                placeholder="Enter comma-separated topics to highlight"
                disabled={isSubmitting}
              />
              <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
                Optional: Topics to highlight in the summary
              </span>
            </Stack>
          ) : (
            <Stack gap="xs">
              <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
                Custom Prompt <span style={{ color: errorColor }}>*</span>
              </label>
              <textarea
                value={formData.customPrompt}
                onChange={(e) => handleChange('customPrompt', e.target.value)}
                placeholder="Describe how you want the content summarized..."
                disabled={isSubmitting}
                rows={5}
                style={{
                  width: '100%',
                  padding: spacing.sm,
                  borderRadius: '6px',
                  border: `1px solid ${theme.colors.border.default}`,
                  fontSize: fontSize.sm,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  background: theme.colors.background.default,
                  color: textColor,
                }}
              />
              {errors.customPrompt && (
                <span style={{ fontSize: fontSize.xs, color: errorColor }}>{errors.customPrompt}</span>
              )}
              <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
                Your prompt guides each segment summary and the final combined summary. Useful for non-English content.
              </span>
            </Stack>
          )}
        </Stack>

        <Stack gap="xs">
          <label style={{ fontSize: fontSize.sm, fontWeight: 500, color: textColor }}>
            Email Recipients
          </label>
          <Input
            value={formData.emailRecipients}
            onChange={(e) => handleChange('emailRecipients', e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            disabled={isSubmitting}
          />
          {errors.emailRecipients && (
            <span style={{ fontSize: fontSize.xs, color: errorColor }}>{errors.emailRecipients}</span>
          )}
          <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
            Optional: Comma-separated email addresses to notify
          </span>
        </Stack>

        <Button
          type="submit"
          variant="solid"
          color="primary"
          disabled={isSubmitting}
          style={{ alignSelf: 'flex-start' }}
        >
          {isSubmitting ? 'Starting Workflow...' : 'Run Workflow'}
        </Button>
      </Stack>
    </form>
  );
}

export default WorkflowForm;
